/*
 * Copyright (c) 2025 - 2026. Bolmate B.V.
 */

import {ErrorType} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import React, {ReactNode} from 'react';

import Invoice from '../models/invoiceModel';

import User from '../models/userModel';

import {BuyBoxEvent, FilterOperator, InvoiceOptionName, SalesColumn} from './enums';

import {AccessKey} from './interfaces';

export type Rating = 1 | 2 | 3 | 4 | 5;

export type ReviewNotificationSettingsImpl = {
    ratings: Set<Rating>;
};

export type BuyBoxNotificationSettingsImpl = {
    buy_box_events: Set<BuyBoxEvent>;
};

export type StockNotificationSettingsImpl = {
    threshold: number;
};

export type InvoiceOption = {
    label: string;
    type: InvoiceOptionName;
    icon: React.ReactElement;
    accessKey: AccessKey | Array<AccessKey>;
    disabled?: boolean;
};

export type InvoiceOptionConfig = Omit<InvoiceOption, 'label'> & {
    label: string | ((invoice: Invoice) => string);
    validator?: (invoice: Invoice, user: User | null) => boolean;
};

export type SelectableReviewProduct = {
    image_url: string;
    title: string;
    ean: string;
};

export type ClauseType = 'include' | 'exclude';
export type ProductSelectionClause = {clause_type: ClauseType};
export type AllProductSelection = ProductSelectionClause & {selection_type: 'all' | 'remaining'};
export type SearchProductSelection = ProductSelectionClause & {selection_type: 'search'; queries: Array<string>};
export type IDProductSelection = ProductSelectionClause & {selection_type: 'id'; ids: Array<string>};

export type ProductSelectionClauseType = AllProductSelection | SearchProductSelection | IDProductSelection;
export type ProductSelection = {
    clauses: Array<ProductSelectionClauseType>;
    invert?: boolean;
};

export type ProductSelectionList = 'available' | 'selected';
export type ProductSelectionParam<VT> = Record<ProductSelectionList, VT>;

export type SubscriptionPricing = {
    orders: number;
    accounts?: number;
    notifications_module?: number;
    total: number;
};

export type NotificationTypeParam = {icon: IconName; title: string; description: string};

export type PeppolStatus = {hasRegistration: boolean; enabled: boolean};

export type EmailStepContentError = {
    reason: 'content';
    step_id: string;
    step_label: string;
    error: string;
};

export type EmailStepOverlapError = {
    reason: 'overlap';
    step1_id: string;
    step1_label: string;
    step2_id: string;
    step2_label: string;
};

export type EmailStepError = EmailStepContentError | EmailStepOverlapError;

export type SalesDashboardStats = {
    num_orders: number;
    quantity: number;
    revenue_excl: number;
    revenue_incl: number;
    vat_percentage: number;
    vat_amount: number;
    sales_14d: number;
    organic_sales: number;
    ad_cost: number;
    sponsored_sales: number;
    acos: number;
    cpc: number;
    costs: number;
    return_costs: number;
    commission: number;
    return_quantity: number;
    returned_revenue_excl: number;
    returned_vat_amount: number;
    returned_commission: number;
    profit: number | null;
    margin: number | null;
    stock: number;
    comparison: SalesDashboardStats | null;
};

export type GeneralSalesProduct = SalesDashboardStats & {
    ean: string;
    title: string | null;
    image_url: string | null;
    global_id?: string | null;
};

export type SpecificSalesProduct = Omit<
    GeneralSalesProduct,
    'title' | 'ean' | 'image_url' | 'comparing_revenue' | 'comparing_acos'
> & {
    group_date: string | IDate;
};

export type AggregatedSalesProduct = {
    periodRevenueExcl: number;
    periodRevenueIncl: number;
    periodRevenueOrganic: number;
    periodRevenueSponsored: number;
    periodQuantity: number;
    periodCosts: number;
    periodProfit: number;
    periodMargin: number;
    periodReturnedQuantity: number;
    periodReturnedRevenue: number;
};

export type SalesDashboardGraphData = {
    labels: Array<string>;
    revenueValues: Array<number>;
    cumulativeRevenueValues: Array<number>;
    organicRevenueValues: Array<number>;
    cumulativeOrganicRevenueValues: Array<number>;
    sponsoredRevenueValues: Array<number>;
    cumulativeSponsoredRevenueValues: Array<number>;
    quantityValues: Array<number>;
    cumulativeQuantityValues: Array<number>;
    costsValues: Array<number>;
    cumulativeCostsValues: Array<number>;
    profitValues: Array<number>;
    cumulativeProfitValues: Array<number>;
    returnedQuantityValues: Array<number>;
    cumulativeReturnedQuantityValues: Array<number>;
    returnedRevenueValues: Array<number>;
    cumulativeReturnedRevenueValues: Array<number>;
};

export type SalesDashboardFilter = {
    id: string;
    operator: FilterOperator;
    values: Array<number>;
    valueSet?: boolean;
};

export type SalesDashboardFilterGroup = {
    field: SalesColumn;
    filters: Array<SalesDashboardFilter>;
    group_type: 'AND' | 'OR';
};

export type ProductCostsErrors = {
    minQuantity: ErrorType;
};

export type ProductDimensions = {
    width_mm: number | null;
    height_mm: number | null;
    length_mm: number | null;
    weight_mg: number | null;
    content_ml?: number | null;
    belt_size_mm?: number | null;
};

export type PortalSelectGroup<VT = string> = {groupLabel: string; items: Array<PortalSelectOption<VT>>};
export type PortalSelectOption<VT = string> = {
    value: VT;
    label: ReactNode;
    selectedLabel?: ReactNode; // Shown when the item is selected
    disabled?: boolean;
    selectable?: boolean;
    // Action items (e.g. "+ Sales account") stay clickable and fire onChange, but
    // never become the selected value — otherwise a click whose side effect the
    // parent doesn't accept (e.g. cancelling the add-account flow) would leave the
    // row stuck as "selected", since the controlled value prop never changed.
    action?: boolean;
};
export type PortalSelectItem<VT = string> = PortalSelectOption<VT> | PortalSelectGroup<VT>;
export type PortalSelectValue<VT = string> = PortalSelectOption<VT>['value'] | Array<PortalSelectOption<VT>['value']>;

export function portalItemIsGroup<VT>(item: PortalSelectItem<VT>): item is PortalSelectGroup<VT> {
    return Object.hasOwn(item, 'groupLabel');
}

export type BolDiscount = {
    title: string;
    amount_incl: number;
    amount_excl: number;
};

export type BolmateControlColor = 'blue' | 'red' | 'yellow' | 'green' | 'purple' | 'gold' | 'notSet' | 'orange';
export type BolmateControlSize = 'small' | 'normal' | 'big';
export type BolmateControlShape = 'square' | 'rounded';
export type BolmateControlAlign = 'left' | 'center' | 'right';

// SEARCH RANKINGS
export type SuggestedKeyword = {
    keyword: string;
    accepted?: boolean;
};
export type SearchRankingsSortKey = 'label' | 'volume' | 'winning_bid' | 'week' | 'month';

export type SearchRankingMode = 'average' | 'best';

export type BolmateCollapsibleSectionSize = 'small' | 'normal' | 'big';

export type BolmateTabsVariant = 'underline' | 'filled';

export type BolmatePlotType = 'line' | 'area' | 'bar';

export type StatTrend = 'up' | 'down' | 'neutral';

export type TranslateFunction = (key: string, fallback: string) => string;

export type PeriodRangeFunc = () => [IDate | null, IDate | null];
